import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  MessageSquare, Send, ArrowLeft, Search, Loader2, UserCheck,
  Clock, AlertTriangle, CheckCircle, XCircle, Eye, Lock,
  ChevronDown, BarChart3, Users, Timer, Bell, Star
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'text-blue-400', bg: 'bg-blue-500/15', dot: 'bg-blue-500' },
  in_progress: { label: 'In Progress', color: 'text-amber-400', bg: 'bg-amber-500/15', dot: 'bg-amber-500' },
  waiting_for_customer: { label: 'Waiting', color: 'text-purple-400', bg: 'bg-purple-500/15', dot: 'bg-purple-500' },
  escalated: { label: 'Escalated', color: 'text-red-400', bg: 'bg-red-500/15', dot: 'bg-red-500' },
  resolved: { label: 'Resolved', color: 'text-emerald-400', bg: 'bg-emerald-500/15', dot: 'bg-emerald-500' },
  closed: { label: 'Closed', color: 'text-gray-400', bg: 'bg-gray-500/15', dot: 'bg-gray-500' },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'text-gray-400', bg: 'bg-gray-500/15' },
  normal: { label: 'Normal', color: 'text-blue-400', bg: 'bg-blue-500/15' },
  high: { label: 'High', color: 'text-amber-400', bg: 'bg-amber-500/15' },
  urgent: { label: 'Urgent', color: 'text-red-400', bg: 'bg-red-500/15' },
};

const CATEGORY_LABELS = {
  payments_not_received: 'Payments', transaction_verification: 'TX Verify',
  billing_invoices: 'Billing', api_integration: 'API', account_access: 'Account',
  refund_request: 'Refund', technical_issue: 'Technical', other: 'Other',
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${c.bg} ${c.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} /> {c.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const c = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${c.bg} ${c.color}`}>{c.label}</span>;
};

// ── Admin Ticket Detail ─────────────────────────────────
const AdminTicketDetail = ({ ticketId, onBack }) => {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sla, setSla] = useState(null);
  const [linked, setLinked] = useState({});
  const [macros, setMacros] = useState([]);
  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMacros, setShowMacros] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchTicket = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tickets/admin/${ticketId}`);
      setTicket(data.ticket);
      setMessages(data.messages);
      setSla(data.sla);
      setLinked(data.linked || {});
    } catch { toast.error('Failed to load ticket'); }
    setLoading(false);
  }, [ticketId]);

  const fetchMacros = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/tickets/admin/macros/list`); setMacros(data.macros || []); } catch {}
  }, []);

  useEffect(() => { fetchTicket(); fetchMacros(); }, [fetchTicket, fetchMacros]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/tickets/admin/${ticketId}/reply`, { message: reply.trim(), is_internal: isInternal });
      setReply('');
      setIsInternal(false);
      fetchTicket();
      toast.success(isInternal ? 'Internal note added' : 'Reply sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const handleStatusChange = async (status) => {
    try {
      await axios.put(`${API}/tickets/admin/${ticketId}/status`, { status });
      fetchTicket();
      toast.success(`Status changed to ${STATUS_CONFIG[status]?.label || status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAssign = async () => {
    try {
      await axios.put(`${API}/tickets/admin/${ticketId}/assign`, {});
      fetchTicket();
      toast.success('Ticket assigned to you');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!ticket) return <div className="text-center py-12 text-muted-foreground">Ticket not found</div>;

  const firstResponseTime = ticket.first_response_at
    ? `${Math.round((new Date(ticket.first_response_at) - new Date(ticket.created_at)) / 60000)} min`
    : 'Pending';

  return (
    <div className="space-y-4" data-testid="admin-ticket-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>

      {/* Ticket Header */}
      <Card className="border-border bg-card">
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs font-mono text-muted-foreground mb-1">{ticket.id}</p>
              <h2 className="text-lg font-bold text-foreground">{ticket.subject}</h2>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <StatusBadge status={ticket.status} />
                <PriorityBadge priority={ticket.priority} />
                <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted/30 rounded">{CATEGORY_LABELS[ticket.category] || ticket.category}</span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span>From: <span className="text-foreground font-medium">{ticket.merchant_name}</span> ({ticket.merchant_email})</span>
                <span>Created: {new Date(ticket.created_at).toLocaleString()}</span>
              </div>
              {(ticket.related_invoice_id || ticket.related_transaction_id || ticket.wallet_address) && (
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {ticket.related_invoice_id && <span className="px-2 py-1 bg-indigo-500/10 rounded text-indigo-400">Invoice: {ticket.related_invoice_id}</span>}
                  {ticket.related_transaction_id && <span className="px-2 py-1 bg-cyan-500/10 rounded text-cyan-400 font-mono">TX: {ticket.related_transaction_id.slice(0, 20)}...</span>}
                  {ticket.wallet_address && <span className="px-2 py-1 bg-amber-500/10 rounded text-amber-400 font-mono">Wallet: {ticket.wallet_address.slice(0, 20)}...</span>}
                </div>
              )}
              {/* Linked Records */}
              {(linked.invoice || linked.transaction) && (
                <div className="mt-2 space-y-1.5">
                  {linked.invoice && (
                    <div className="px-3 py-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-xs" data-testid="admin-linked-invoice">
                      <span className="font-semibold text-indigo-400">Linked Invoice: </span>
                      <span className="text-foreground">{linked.invoice.id}</span>
                      <span className="text-muted-foreground ml-2">Amount: ${linked.invoice.amount || linked.invoice.amount_due}</span>
                      <span className="text-muted-foreground ml-2">Status: {linked.invoice.status}</span>
                    </div>
                  )}
                  {linked.transaction && (
                    <div className="px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-xs" data-testid="admin-linked-tx">
                      <span className="font-semibold text-cyan-400">Linked TX: </span>
                      <span className="text-foreground font-mono">{(linked.transaction.tx_hash || '').slice(0, 24)}...</span>
                      <span className="text-muted-foreground ml-2">{linked.transaction.amount} {linked.transaction.currency}</span>
                      <span className="text-muted-foreground ml-2">Status: {linked.transaction.status}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* SLA Info */}
            <div className="text-right space-y-1 shrink-0">
              {sla && (
                <>
                  <p className={`text-[10px] px-2 py-0.5 rounded inline-block ${sla.first_response_breached ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    1st Response: {sla.first_response_elapsed_hours ?? '—'}h / {sla.first_response_target_hours}h
                  </p>
                  <br/>
                  <p className={`text-[10px] px-2 py-0.5 rounded inline-block mt-1 ${sla.resolution_breached ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    Resolution: {sla.resolution_elapsed_hours ?? '—'}h / {sla.resolution_target_hours}h
                  </p>
                </>
              )}
              {ticket.assigned_name && <p className="text-[10px] text-muted-foreground">Assigned: <span className="text-foreground">{ticket.assigned_name}</span></p>}
              <p className="text-[10px] text-muted-foreground">{ticket.message_count} messages</p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground mr-1">Status:</span>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <button key={key} onClick={() => handleStatusChange(key)}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-all border ${ticket.status === key ? `${cfg.bg} ${cfg.color} border-current` : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`}
                data-testid={`set-status-${key}`}>{cfg.label}</button>
            ))}
            <div className="ml-auto">
              {!ticket.assigned_name && (
                <Button size="sm" variant="outline" onClick={handleAssign} className="h-7 text-xs" data-testid="assign-to-me">
                  <UserCheck className="h-3 w-3 mr-1" /> Assign to Me
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card className="border-border bg-card">
        <CardContent className="py-4">
          <div className="space-y-4 max-h-[450px] overflow-y-auto pr-2">
            {messages.map(msg => {
              const isAdmin = msg.sender_role === 'admin';
              const isIntNote = msg.is_internal;
              return (
                <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.id}`}>
                  <div className={`max-w-[75%] rounded-xl px-4 py-3 ${
                    isIntNote ? 'bg-amber-500/10 border border-amber-500/20 border-dashed' :
                    isAdmin ? 'bg-emerald-600/15 border border-emerald-500/20' :
                    'bg-muted/30 border border-border'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold ${isIntNote ? 'text-amber-400' : isAdmin ? 'text-emerald-400' : 'text-indigo-400'}`}>
                        {isIntNote && <Lock className="h-3 w-3 inline mr-1" />}
                        {msg.sender_name || (isAdmin ? 'Admin' : 'Customer')}
                      </span>
                      {isIntNote && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-semibold">INTERNAL NOTE</span>}
                      <span className="text-[10px] text-muted-foreground">{new Date(msg.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply */}
          <div className="mt-4 pt-4 border-t border-border">
            {/* Macros */}
            <div className="mb-3 relative">
              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setShowMacros(!showMacros)} data-testid="macros-toggle">
                <ChevronDown className={`h-3 w-3 mr-1 transition-transform ${showMacros ? 'rotate-180' : ''}`} />
                Quick Responses
              </Button>
              {showMacros && (
                <div className="mt-1 bg-card border border-border rounded-lg p-2 space-y-1 max-h-40 overflow-y-auto">
                  {macros.map(m => (
                    <button key={m.id} className="w-full text-left px-3 py-1.5 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                      onClick={() => { setReply(m.content); setShowMacros(false); }} data-testid={`macro-${m.id}`}>
                      <span className="font-medium text-foreground">{m.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 mb-2">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)}
                  className="rounded border-border" data-testid="internal-note-checkbox" />
                <Lock className="h-3 w-3 text-amber-400" />
                <span className="text-xs text-muted-foreground">Internal Note</span>
              </label>
              {isInternal && <span className="text-[10px] text-amber-400">(Not visible to customer)</span>}
            </div>

            <Textarea rows={3} placeholder={isInternal ? 'Add internal note...' : 'Reply to customer...'} value={reply}
              onChange={e => setReply(e.target.value)} data-testid="admin-reply-input"
              className={isInternal ? 'border-amber-500/30' : ''}
              onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) handleSendReply(); }} />
            <div className="flex justify-end mt-2">
              <Button onClick={handleSendReply} disabled={sending || !reply.trim()}
                className={isInternal ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'} data-testid="admin-send-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : isInternal ? <Lock className="h-4 w-4 mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                {isInternal ? 'Add Note' : 'Send Reply'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ── Main Admin Support Page ─────────────────────────────
const AdminSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [csatData, setCsatData] = useState(null);
  const [notifLogs, setNotifLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tab, setTab] = useState('tickets'); // 'tickets' | 'analytics' | 'alerts'
  const [filters, setFilters] = useState({ status: '', priority: '', category: '', search: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [telegramConfig, setTelegramConfig] = useState({ bot_token: '', chat_id: '', enabled: false });
  const [alertConfig, setAlertConfig] = useState({ alert_email: '', telegram_enabled: false, email_enabled: false });
  const [savingConfig, setSavingConfig] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      const params = { page, limit: 20 };
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;
      const { data } = await axios.get(`${API}/tickets/admin/all`, { params });
      setTickets(data.data || []);
      setTotalPages(data.pages || 0);
    } catch {}
    setLoading(false);
  }, [page, filters]);

  const fetchStats = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/tickets/admin/stats`); setStats(data); } catch {}
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/tickets/admin/analytics/overview`); setAnalytics(data); } catch {}
  }, []);

  const fetchCsat = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/tickets/admin/analytics/csat`); setCsatData(data); } catch {}
  }, []);

  const fetchAlertConfigs = useCallback(async () => {
    try {
      const [tg, al] = await Promise.all([
        axios.get(`${API}/tickets/admin/config/telegram`),
        axios.get(`${API}/tickets/admin/config/alerts`),
      ]);
      setTelegramConfig(tg.data);
      setAlertConfig(al.data);
    } catch {}
  }, []);

  const fetchNotifLogs = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/tickets/admin/notification-log`, { params: { limit: 20 } }); setNotifLogs(data.data || []); } catch {}
  }, []);

  useEffect(() => { fetchTickets(); fetchStats(); }, [fetchTickets, fetchStats]);
  useEffect(() => { if (tab === 'analytics') { fetchAnalytics(); fetchCsat(); } }, [tab, fetchAnalytics, fetchCsat]);
  useEffect(() => { if (tab === 'alerts') { fetchAlertConfigs(); fetchNotifLogs(); } }, [tab, fetchAlertConfigs, fetchNotifLogs]);

  const saveTelegram = async () => {
    setSavingConfig(true);
    try { await axios.put(`${API}/tickets/admin/config/telegram`, telegramConfig); toast.success('Telegram config saved'); } catch { toast.error('Failed'); }
    setSavingConfig(false);
  };
  const saveAlerts = async () => {
    setSavingConfig(true);
    try { await axios.put(`${API}/tickets/admin/config/alerts`, alertConfig); toast.success('Alert config saved'); } catch { toast.error('Failed'); }
    setSavingConfig(false);
  };
  const testTelegram = async () => {
    try { await axios.post(`${API}/tickets/admin/config/telegram/test`); toast.success('Test message sent!'); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const detectChatId = async () => {
    try {
      const { data } = await axios.post(`${API}/tickets/admin/config/telegram/detect-chat`);
      setTelegramConfig(p => ({ ...p, chat_id: data.chat_id }));
      toast.success(`Chat ID detected: ${data.chat_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Detection failed'); }
  };

  if (selectedTicket) {
    return (
      <AdminLayout title="Ticket Detail">
        <div className="p-6 max-w-4xl mx-auto">
          <AdminTicketDetail ticketId={selectedTicket} onBack={() => { setSelectedTicket(null); fetchTickets(); fetchStats(); }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Support Tickets">
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Support Tickets</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Manage and resolve customer issues</p>
          </div>
          <div className="flex gap-1 bg-[hsl(var(--card))] border border-border rounded-lg p-1">
            {[['tickets', 'Tickets'], ['analytics', 'Analytics'], ['alerts', 'Alert Config']].map(([v, l]) => (
              <button key={v} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${tab === v ? 'bg-indigo-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setTab(v)} data-testid={`admin-tab-${v}`}>{l}</button>
            ))}
          </div>
        </div>

        {tab === 'tickets' && (
          <>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="border-border bg-card"><CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
              <p className="text-[10px] text-muted-foreground">Total</p>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.by_status?.open || 0}</p>
              <p className="text-[10px] text-muted-foreground">Open</p>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-amber-400">{stats.by_status?.in_progress || 0}</p>
              <p className="text-[10px] text-muted-foreground">In Progress</p>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-red-400">{(stats.by_priority?.urgent || 0) + (stats.by_priority?.high || 0)}</p>
              <p className="text-[10px] text-muted-foreground">High/Urgent</p>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">{stats.avg_resolution_hours ? `${stats.avg_resolution_hours}h` : '—'}</p>
              <p className="text-[10px] text-muted-foreground">Avg Resolution</p>
            </CardContent></Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search tickets..." value={filters.search} className="h-8 w-52 text-xs"
              onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') fetchTickets(); }} data-testid="admin-ticket-search" />
          </div>
          <select className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground" value={filters.status}
            onChange={e => { setFilters(p => ({ ...p, status: e.target.value })); setPage(1); }} data-testid="filter-status">
            <option value="">All Status</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground" value={filters.priority}
            onChange={e => { setFilters(p => ({ ...p, priority: e.target.value })); setPage(1); }} data-testid="filter-priority">
            <option value="">All Priority</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground" value={filters.category}
            onChange={e => { setFilters(p => ({ ...p, category: e.target.value })); setPage(1); }} data-testid="filter-category">
            <option value="">All Categories</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        {/* Ticket Table */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : tickets.length === 0 ? (
          <Card className="border-border bg-card"><CardContent className="py-12 text-center text-muted-foreground">
            <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
            No tickets match your filters
          </CardContent></Card>
        ) : (
          <div className="bg-[hsl(var(--card))] border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-tickets-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-muted-foreground text-[10px] uppercase tracking-wider">
                    <th className="text-left py-3 px-4 font-medium">Ticket</th>
                    <th className="text-left py-3 px-4 font-medium">Merchant</th>
                    <th className="text-left py-3 px-4 font-medium">Category</th>
                    <th className="text-left py-3 px-4 font-medium">Priority</th>
                    <th className="text-left py-3 px-4 font-medium">Status</th>
                    <th className="text-left py-3 px-4 font-medium">Assigned</th>
                    <th className="text-left py-3 px-4 font-medium">Last Reply</th>
                    <th className="text-left py-3 px-4 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(t => (
                    <tr key={t.id} className="border-b border-border/40 hover:bg-muted/10" data-testid={`admin-ticket-${t.id}`}>
                      <td className="py-3 px-4">
                        <p className="text-xs font-mono text-muted-foreground">{t.id}</p>
                        <p className="text-sm font-medium text-foreground truncate max-w-[200px]">{t.subject}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-xs text-foreground">{t.merchant_name}</p>
                        <p className="text-[10px] text-muted-foreground">{t.merchant_email}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs px-2 py-0.5 rounded bg-muted/30 text-muted-foreground">{CATEGORY_LABELS[t.category] || t.category}</span>
                      </td>
                      <td className="py-3 px-4"><PriorityBadge priority={t.priority} /></td>
                      <td className="py-3 px-4"><StatusBadge status={t.status} /></td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-muted-foreground">{t.assigned_name || '—'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-xs text-muted-foreground">{t.last_reply_by === 'admin' ? 'Admin' : 'Customer'}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(t.last_reply_at).toLocaleDateString()}</p>
                      </td>
                      <td className="py-3 px-4">
                        <Button size="sm" variant="ghost" className="h-7 text-xs"
                          onClick={() => setSelectedTicket(t.id)} data-testid={`view-ticket-${t.id}`}>
                          <Eye className="h-3 w-3 mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-3 border-t border-border">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">Prev</Button>
                <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
                <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">Next</Button>
              </div>
            )}
          </div>
        )}
          </>
        )}

        {/* Analytics Tab */}
        {tab === 'analytics' && analytics && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="border-border bg-card"><CardContent className="py-3 text-center">
                <p className={`text-2xl font-bold ${analytics.sla.first_response_compliance >= 90 ? 'text-emerald-400' : 'text-red-400'}`}>{analytics.sla.first_response_compliance}%</p>
                <p className="text-[10px] text-muted-foreground">1st Response SLA</p>
              </CardContent></Card>
              <Card className="border-border bg-card"><CardContent className="py-3 text-center">
                <p className={`text-2xl font-bold ${analytics.sla.resolution_compliance >= 90 ? 'text-emerald-400' : 'text-red-400'}`}>{analytics.sla.resolution_compliance}%</p>
                <p className="text-[10px] text-muted-foreground">Resolution SLA</p>
              </CardContent></Card>
              <Card className="border-border bg-card"><CardContent className="py-3 text-center">
                <p className="text-2xl font-bold text-red-400">{analytics.sla.first_response_breached + analytics.sla.resolution_breached}</p>
                <p className="text-[10px] text-muted-foreground">Total Breaches</p>
              </CardContent></Card>
              <Card className="border-border bg-card"><CardContent className="py-3 text-center">
                <p className="text-2xl font-bold text-foreground">{analytics.active_count}</p>
                <p className="text-[10px] text-muted-foreground">Active Tickets</p>
              </CardContent></Card>
            </div>
            <Card className="border-border bg-card"><CardContent className="py-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">SLA Rules</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(analytics.sla_rules || {}).map(([p, r]) => (
                  <div key={p} className="px-3 py-2 rounded-lg bg-muted/20 text-center">
                    <p className="text-xs font-semibold text-foreground capitalize">{p}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Response: {r.first_response}h</p>
                    <p className="text-[10px] text-muted-foreground">Resolution: {r.resolution}h</p>
                  </div>
                ))}
              </div>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Tickets by Category</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(analytics.by_category || {}).map(([cat, cnt]) => (
                  <div key={cat} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/20">
                    <span className="text-xs text-muted-foreground">{CATEGORY_LABELS[cat] || cat}</span>
                    <span className="text-sm font-bold text-foreground">{cnt}</span>
                  </div>
                ))}
              </div>
            </CardContent></Card>
            {analytics.sla_breached_tickets?.length > 0 && (
              <Card className="border-red-500/20 bg-red-500/5"><CardContent className="py-4">
                <h3 className="text-sm font-semibold text-red-400 mb-3">SLA Breached Tickets</h3>
                <div className="space-y-2">
                  {analytics.sla_breached_tickets.map(t => (
                    <div key={t.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/15" data-testid={`sla-breach-${t.id}`}>
                      <div>
                        <span className="text-xs font-mono text-muted-foreground">{t.id}</span>
                        <p className="text-sm text-foreground">{t.subject}</p>
                      </div>
                      <div className="text-right">
                        <PriorityBadge priority={t.priority} />
                        {t.first_response_breached && <p className="text-[10px] text-red-400 mt-0.5">Response: {t.first_response_hours}h</p>}
                        {t.resolution_breached && <p className="text-[10px] text-red-400">Resolution: {t.resolution_hours}h</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            )}
            {Object.keys(analytics.agent_performance || {}).length > 0 && (
              <Card className="border-border bg-card"><CardContent className="py-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">Agent Performance</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(analytics.agent_performance).map(([agent, count]) => (
                    <div key={agent} className="px-3 py-2 rounded-lg bg-muted/20 text-center">
                      <p className="text-xs font-medium text-foreground">{agent}</p>
                      <p className="text-lg font-bold text-emerald-400">{count}</p>
                      <p className="text-[10px] text-muted-foreground">resolved</p>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            )}
            {/* CSAT Section */}
            {csatData && (
              <Card className="border-border bg-card"><CardContent className="py-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">Customer Satisfaction (CSAT)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="px-3 py-2 rounded-lg bg-muted/20 text-center">
                    <p className="text-2xl font-bold text-amber-400">{csatData.avg_rating > 0 ? `${csatData.avg_rating}/5` : '—'}</p>
                    <p className="text-[10px] text-muted-foreground">Avg Rating</p>
                  </div>
                  <div className="px-3 py-2 rounded-lg bg-muted/20 text-center">
                    <p className="text-2xl font-bold text-foreground">{csatData.total_responses}</p>
                    <p className="text-[10px] text-muted-foreground">Responses</p>
                  </div>
                  <div className="px-3 py-2 rounded-lg bg-muted/20 text-center">
                    <p className="text-2xl font-bold text-foreground">{csatData.response_rate}%</p>
                    <p className="text-[10px] text-muted-foreground">Response Rate</p>
                  </div>
                  <div className="px-3 py-2 rounded-lg bg-muted/20 text-center">
                    <div className="flex justify-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(s => (
                        <span key={s} className="text-xs">{csatData.rating_distribution?.[s] || 0}</span>
                      ))}
                    </div>
                    <div className="flex justify-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(s => (
                        <span key={s} className="text-amber-400 text-[10px]">&#9733;</span>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground">Distribution</p>
                  </div>
                </div>
                {Object.keys(csatData.by_agent || {}).length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">By Agent</p>
                    <div className="flex gap-3 flex-wrap">
                      {Object.entries(csatData.by_agent).map(([agent, rating]) => (
                        <div key={agent} className="px-3 py-1.5 rounded bg-muted/20 text-xs">
                          <span className="text-foreground font-medium">{agent}</span>
                          <span className="text-amber-400 ml-2">{rating}/5 &#9733;</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {csatData.recent?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Recent Feedback</p>
                    <div className="space-y-1.5">
                      {csatData.recent.slice(0, 5).map((r, i) => (
                        <div key={i} className="flex items-center gap-3 px-3 py-1.5 rounded bg-muted/10 text-xs">
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map(s => (
                              <span key={s} className={s <= r.rating ? 'text-amber-400' : 'text-muted-foreground/30'}>&#9733;</span>
                            ))}
                          </div>
                          <span className="text-muted-foreground">{r.merchant_email}</span>
                          {r.comment && <span className="text-foreground italic">"{r.comment}"</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent></Card>
            )}
          </div>
        )}
        {tab === 'analytics' && !analytics && <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}

        {/* Alert Config Tab */}
        {tab === 'alerts' && (
          <div className="space-y-5 max-w-2xl">
            <Card className="border-border bg-card"><CardContent className="py-4">
              <h3 className="text-sm font-semibold text-foreground mb-2">How to Set Up Telegram Alerts</h3>
              <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                <li>Open Telegram and search for <span className="text-foreground font-semibold">@BotFather</span></li>
                <li>Send <code className="px-1 py-0.5 rounded bg-muted text-foreground">/newbot</code> and follow the prompts to name your bot</li>
                <li>Copy the <span className="text-foreground font-semibold">Bot Token</span> provided by BotFather</li>
                <li>Add your bot to a group or start a DM with it</li>
                <li>To get your <span className="text-foreground font-semibold">Chat ID</span>: send a message to the bot, then visit <code className="px-1 py-0.5 rounded bg-muted text-foreground text-[10px]">api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code></li>
                <li>Find the <code className="px-1 py-0.5 rounded bg-muted text-foreground">chat.id</code> value in the response</li>
              </ol>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Telegram Bot Configuration</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Bot Token</label>
                  <Input placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11" value={telegramConfig.bot_token}
                    onChange={e => setTelegramConfig(p => ({ ...p, bot_token: e.target.value }))} data-testid="tg-bot-token" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Chat ID</label>
                  <div className="flex gap-2">
                    <Input placeholder="-100123456789" value={telegramConfig.chat_id}
                      onChange={e => setTelegramConfig(p => ({ ...p, chat_id: e.target.value }))} data-testid="tg-chat-id" />
                    <Button size="sm" variant="outline" onClick={detectChatId} className="shrink-0 text-xs" data-testid="detect-chat-id-btn">
                      Detect Chat ID
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Send a message to your bot first, then click Detect</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={telegramConfig.enabled} onChange={e => setTelegramConfig(p => ({ ...p, enabled: e.target.checked }))} data-testid="tg-enabled" />
                  <span className="text-xs text-foreground">Enable Telegram Alerts</span>
                </label>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveTelegram} disabled={savingConfig} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-tg-btn">Save</Button>
                  <Button size="sm" variant="outline" onClick={testTelegram} disabled={!telegramConfig.enabled || !telegramConfig.bot_token} data-testid="test-tg-btn">Test Connection</Button>
                </div>
              </div>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Email Alert Configuration</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Admin Alert Email</label>
                  <Input placeholder="admin@yourcompany.com" value={alertConfig.alert_email}
                    onChange={e => setAlertConfig(p => ({ ...p, alert_email: e.target.value }))} data-testid="alert-email" />
                  <p className="text-[10px] text-muted-foreground mt-1">Receives: new tickets, SLA breaches, node down events</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={alertConfig.email_enabled} onChange={e => setAlertConfig(p => ({ ...p, email_enabled: e.target.checked }))} data-testid="email-enabled" />
                  <span className="text-xs text-foreground">Enable Email Alerts</span>
                </label>
                <Button size="sm" onClick={saveAlerts} disabled={savingConfig} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-alerts-btn">Save Alert Config</Button>
              </div>
            </CardContent></Card>
            {/* Notification Log */}
            <Card className="border-border bg-card"><CardContent className="py-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Recent Notification Log</h3>
              {notifLogs.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No notifications sent yet. Alerts will appear here once Telegram/Email is configured and events trigger.</p>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto" data-testid="notif-log-list">
                  {notifLogs.map((log, i) => (
                    <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs ${log.success ? 'bg-emerald-500/5 border border-emerald-500/10' : 'bg-red-500/5 border border-red-500/10'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${log.success ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${log.channel === 'telegram' ? 'bg-cyan-500/15 text-cyan-400' : 'bg-indigo-500/15 text-indigo-400'}`}>
                        {log.channel}
                      </span>
                      <span className="px-1.5 py-0.5 rounded bg-muted/30 text-muted-foreground text-[10px]">{log.event_type}</span>
                      <span className="text-muted-foreground truncate flex-1">{log.message_preview}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSupport;
